import { Injectable } from '@angular/core';

/**
 * Our abstraction over localStorage and sessionStorage,
 *  - typed and versioned
 *  - with safe storage of organization-related items
 *  - with try/catch on storage access
 */

interface GlobalContainer<T> {
    version: number;
    data: T;
}

interface OrganizationContainer<T> {
    orgId: string;
    version: number;
    data: T;
}

export enum LocalStorageKeys {
    currentUser = 'currentUser',
    cockpitState = 'cockpitState',
    config = 'config',
    prevSkin = 'prevSkin',
    HEADERS_LOCALSTORAGE_KEY = 'consolidated_view_columns',
    sudoMod = 'sudoMod',
    selectedDedicatedAdminId = 'selectedDedicatedAdminId',
    selectedOrgId = 'selectedOrgId',
    dcarlist = 'dcarlist_',
    layoutConfig = 'layoutConfig',
    localeUser = 'localeUser',
    advancedMeasureleftPanelState = 'advancedMeasureleftPanelState',
    measureSummaryDetails = 'measureSummaryDetails',
    resultsSelectedEntities = 'resultsSelectedEntities',
    cookieConsent = 'cookieConsent',
}

export type StorageType = 'local' | 'session';

@Injectable({
    providedIn: 'root',
})
export class StorageService {
    private getStorage(type: StorageType): Storage {
        return type === 'session' ? sessionStorage : localStorage;
    }

    private getStorageName(type: StorageType): string {
        return type === 'session' ? 'sessionStorage' : 'localStorage';
    }

    /**
     * Get an item from storage, globally (not related to an organization)
     * @param key
     * @param version Expected version, discard if found incorrect
     * @param keyPrecision Optional key suffix
     * @param storageType 'local' or 'session' (default: 'local')
     * @returns T if found for the version
     */
    public getGlobalItem<T>(
        mainKey: LocalStorageKeys,
        version: number = 1,
        keyPrecision?: string,
        storageType: StorageType = 'local',
    ): T | undefined {
        const storage = this.getStorage(storageType);
        const storageName = this.getStorageName(storageType);
        const key = keyPrecision ? mainKey + keyPrecision : mainKey;

        try {
            const json = storage.getItem(key);
            if (json) {
                try {
                    const container = <GlobalContainer<T>>JSON.parse(json);
                    if (container.version === version && 'data' in container) {
                        return container.data;
                    }
                } catch (_err) {
                    console.error(`getGlobalItem() Error on JSON parse ${key}`);
                }
                // Cleanup bad json or version mismatch
                storage.removeItem(key);
            }
        } catch (err) {
            console.warn(`Cannot read from ${storageName}: ` + err);
        }
        return undefined;
    }

    /**
     * Store an item in storage, globally (not related to an organization)
     * @param key
     * @param data
     * @param version
     * @param keyPrecision Optional key suffix
     * @param storageType 'local' or 'session' (default: 'local')
     */
    public setGlobalItem<T>(
        mainKey: LocalStorageKeys,
        data: T,
        version: number = 1,
        keyPrecision?: string,
        storageType: StorageType = 'local',
    ) {
        const storage = this.getStorage(storageType);
        const storageName = this.getStorageName(storageType);
        const key = keyPrecision ? mainKey + keyPrecision : mainKey;
        const container: GlobalContainer<T> = { version, data };

        try {
            storage.setItem(key, JSON.stringify(container));
        } catch (err) {
            // Could be QuotaExceeded
            console.warn(`Cannot write to ${storageName}: ` + err);
        }
    }

    /**
     * Remove a key from storage
     * @param key
     * @param storageType 'local' or 'session' (default: 'local')
     */
    public removeItem(key: LocalStorageKeys, storageType: StorageType = 'local') {
        const storage = this.getStorage(storageType);
        try {
            storage.removeItem(key);
        } catch {
            // ignore
        }
    }

    // ==================== ORGANIZATION STORAGE (localStorage only) ====================

    /**
     * Get an item from storage for an organization
     * @param organisationId
     * @param key
     * @param version Expected version, discard if found incorrect
     * @returns T if found for the organization & version
     */
    public getOrganizationItem<T>(organisationId: string, key: LocalStorageKeys, version: number = 1): T | undefined {
        try {
            const json = localStorage.getItem(key);
            if (json) {
                try {
                    const container = <OrganizationContainer<T>>JSON.parse(json);
                    if (container.orgId === organisationId && container.version === version && 'data' in container) {
                        return container.data;
                    }
                } catch (_err) {
                    console.error(`getOrganizationItem() Error on JSON parse ${key}`);
                }
                // Cleanup bad json or version mismatch
                localStorage.removeItem(key);
            }
        } catch (err) {
            console.warn('Cannot read from localStorage: ' + err);
        }
        return undefined;
    }

    /**
     * Store an item in storage for an organization
     * @param key
     * @param data
     * @param version
     */
    public setOrganizationItem<T>(organizationId: string, key: LocalStorageKeys, data: T, version: number = 1) {
        const container: OrganizationContainer<T> = {
            orgId: organizationId,
            version,
            data,
        };
        try {
            localStorage.setItem(key, JSON.stringify(container));
        } catch (e) {
            // Could be QuotaExceeded
            console.warn('Cannot write to localStorage: ' + e);
        }
    }
}
